/* eslint-disable no-sequences, @typescript-eslint/no-unused-expressions, no-param-reassign, default-case, func-names */
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { defaultMapOptionsPayload } from './initial-states/defaultMapOptionsPayload';
import {
    DrawPoligonsPayload,
    IFlowPoints,
    IGetMarkersPayload,
    IMapOptions,
    IMapOptionsPayload,
    IMarkMultiplesOnMapPayload,
    IMarkMultiplesOnMapWithInfoWindowPayload,
    IMarkMultiplesOnMapWithoutClusterPayload,
    TMapCoordinates,
    ImarkOnMapWithListenerAndAddressProps,
    IPointWithSituation,
} from './map.types';
import PIN from '../../presentation/assets/markers/MARKER-pin.svg';
import MARKER_green from '../../presentation/assets/markers/MARKER-pin-green.svg';
import MARKER_yellowbaby from '../../presentation/assets/markers/MARKER-pin-yellowbaby.svg';
import MARKER_lightGreen from '../../presentation/assets/markers/MARKER-pin-lightgreen.svg';
import MARKER_primaryred from '../../presentation/assets/markers/MARKER-pin-primaryred.svg';
import MARKER_violeta from '../../presentation/assets/markers/MARKER-pin-violeta.svg';
import MARKER_darkOrange from '../../presentation/assets/markers/MARKER-pin-darkorange.svg';
import MARKER_goldenroddark from '../../presentation/assets/markers/MARKER-pin-goldenroddark.svg';
import MARKER_SPEECH_BUBBLE from '../../presentation/assets/markers/MARKER-speech-bubble.svg';
import MARKER_SPEECH_BUBBLE_BLUE from '../../presentation/assets/markers/MARKER-speech-bubble-blue.svg';
import MARKER_blue from '../../presentation/assets/markers/MARKER-pin-blue.svg';
import DOT_blue from '../../presentation/assets/markers/CIRCLE-blue.svg';
import DOT_green from '../../presentation/assets/markers/CIRCLE-green.svg';
import DOT_yellowbaby from '../../presentation/assets/markers/CIRCLE-yellowbaby.svg';
import DOT from '../../presentation/assets/markers/CIRCLE.svg';
import DOT_redWithBorder from '../../presentation/assets/markers/CIRCLE-red-border.svg';
import DOT_darkOrange from '../../presentation/assets/markers/CIRCLE-darkorange.svg';
import DOT_lightGreen from '../../presentation/assets/markers/CIRCLE-lightgreen.svg';
import DOT_goldenroddark from '../../presentation/assets/markers/CIRCLE-lightorange.svg';
import DOT_violeta from '../../presentation/assets/markers/CIRCLE-violeta.svg';
import DOT_primaryred from '../../presentation/assets/markers/CIRCLE-primaryred.svg';
import {
    appendInfoWindowToMarker,
    attatcher,
    controlEventListinerDrawPoligons,
    controlEventListinerMouseOverPolygon,
    controlEventListinerOnMarker,
    createInfoWindow,
    flowPointAttatcher,
    polygonAddOn,
    updateClusterValue,
} from './map.helpers';
import {
    defaultMapCircleStyles,
    markerClustercOptions,
    markerClustercOptionsSchools,
    polygonFreeHandStyles,
    polygonLine,
    polygonStaticStyles,
} from './map.styles';

export default class Map {
    static map: google.maps.Map;

    static marker: google.maps.Marker;

    static markerCircle: google.maps.Circle;

    static circleRadius = 500;

    static placesService: google.maps.places.PlacesService;

    static geocoder: google.maps.Geocoder;

    static realtyPoints: MarkerClusterer;

    static schoolsPoints: MarkerClusterer;

    static trafficLayer: google.maps.TrafficLayer;

    static drawingManager: google.maps.drawing.DrawingManager;

    static flowPoints: IFlowPoints[] = [];

    static prospectMarkers: google.maps.Marker[] = [];

    static polylineMarkers: google.maps.Marker[] = [];

    static neighborhoodPoligons: google.maps.Polygon[] = [];

    static schoolPoligons: google.maps.Polygon[] = [];

    static PoligonOnMap: google.maps.Polygon;

    static circleOnMap: google.maps.Circle;

    static polylineOnMap: google.maps.Polyline;

    static freehandListener: google.maps.MapsEventListener | null;

    static isochronePoligons: google.maps.Polygon[] = [];

    static peaPoligons: google.maps.Polygon[][] = [];

    static incomePoligons: google.maps.Polygon[][] = [];

    static concurrentsMarker: IFlowPoints[] = [];

    static schoolsMarker: IFlowPoints[] = [];

    static infoWindowPoligons: google.maps.InfoWindow[] = [];

    static currentBounds: any = [];

    static infoWindowFlowPoints: google.maps.InfoWindow[] = [];

    static infoWindowSchoolsMarkers: google.maps.InfoWindow[] = [];

    static infoWindowRealtyMarkers: google.maps.InfoWindow[] = [];

    static panorama: google.maps.StreetViewPanorama;

    // constructor() {}

    static initMap: (payload?: IMapOptionsPayload) => void;

    static loadFromJson: (file: string) => void;

    static loadMultipleFromJsons: (files: Array<string>) => void;

    static markOnMap: (payload: {
        center: TMapCoordinates;
        shouldBeDot: boolean;
        action?: () => void;
    }) => void;

    static markOnMapWithListenerAndAddress: (
        payload: ImarkOnMapWithListenerAndAddressProps
    ) => void;

    static markCircleOnMap: (payload: { center: TMapCoordinates }) => void;

    static setCircleRadius: (newRadius: number) => void;

    static getCircleRadius: () => number;

    static resizeCircleOnMap: (newRadius: number) => void;

    static getMarkers: (payload: IGetMarkersPayload) => google.maps.Marker[];

    static markMultiplesOnMap: (payload: IMarkMultiplesOnMapPayload) => void;

    static removeMarkMultiplesOnMap: (payload: { markerType: number }) => void;

    static addTrafficLayer: () => void;

    static removeTrafficLayer: () => void;

    static drawPoligons: (payload: DrawPoligonsPayload) => void;

    static markMultiplesOnMapWithoutCluster: (
        payload: IMarkMultiplesOnMapWithoutClusterPayload
    ) => void;

    static removeFlowPoints: (payload: { flowPointType: string }) => void;

    static setMapView: (payload: { view: string }) => void;

    static removeProspects: () => void;

    static removePoligons: (type: string, id?: number) => void;

    static createStreetViewDiv: (payload: {
        fenway: { lat: number; lng: number };
        div: HTMLElement;
    }) => void;

    static clearMap: () => void; // TODO this should clear everything on map like a reaload

    static clearDrawings: ({ cleanPea, cleanIncome }: { cleanPea: boolean, cleanIncome: boolean }) => void;

    static stopDrawingManager: (polygon: google.maps.Polygon) => void;

    static stopDrawingManagerPolyline: (polyline: google.maps.Polyline) => void

    static stopDrawingManagerCircle: (circle: google.maps.Circle) => void

    static drawPolygon: (callback?: () => void) => void;

    static drawPolyLine: (callback?: () => void) => void;

    static drawCircle: (callback?: () => void) => void;

    static stopDrawingPolygon: () => void;

    static drawFreeHand: (isMobile: boolean, callback?: () => void) => void;

    static poligonCoordinates: () => google.maps.LatLng[];

    static stopDrawingFreehand: () => void;

    static disableMapMovement: () => void;

    static enableMapMovement: () => void;

    static hideVitaminPolygons: () => void;

    static generateSinglePolygonVitamin: (
        polygonData: any,
        color: any,
        onClick: (poly: any) => void
    ) => void;

    static setVitaminPolygon: (
        vitaminSectors: any,
        color: any,
        action: (arg: any) => void
    ) => void;

    static hideShowVitaminPolygons: () => void;

    static removeVitaminSectors: () => void;

    static concurrents: (
        icon: any,
        coordinates: number[],
        size: number[],
        type: string
    ) => void;

    static removeConcurrents: (type: string) => void;

    static markMultiplesOnMapWithInfoWindow: (
        payload: IMarkMultiplesOnMapWithInfoWindowPayload
    ) => void;

    static getMarkersCustom: (payload: any) => google.maps.Marker[];

    static markMultiplesOnMapWithInfoWindowAndAgroup: (a: any, b: any) => void;

    static removeMultiplesOnMapWithInfoWindowAndAgroup: () => void;

    static plotSquareHeatMap: (drawSquareHeatMapPayload: google.maps.RectangleOptions, heatMapValue?: string) => void;

    static removeSquareHeatMap: () => void;

    static heatMapSquare: google.maps.Rectangle[] = [];

    static heatMap: google.maps.visualization.HeatmapLayer;

    static plotHeatMap: (heatMapOptions?: google.maps.visualization.HeatmapLayerOptions) => void;

    static removeHeatMap: () => void;
}

// initMap initializes the map and sets up its default configuration.
Map.initMap = function (payload?: IMapOptionsPayload) {
    const mapsDivId = payload?.mapsDivId || defaultMapOptionsPayload.mapsDivId;
    const mapOptions: IMapOptions = {
        ...defaultMapOptionsPayload.mapOptions,
        ...payload?.mapOptions,
    }; // payload?.mapOptions || defaultMapOptionsPayload.mapOptions;

    const newMap = new google.maps.Map(
        document.getElementById(mapsDivId) as HTMLElement,
        mapOptions as google.maps.MapOptions
    );

    Map.currentBounds = payload?.bounds;

    Map.map = newMap;
    Map.geocoder = new google.maps.Geocoder();
    Map.drawingManager = new google.maps.drawing.DrawingManager({
        drawingControl: false,
        polygonOptions: {
            editable: false,
        },
        polylineOptions: {
            clickable: false,
            strokeColor: "#333",
            editable: true,
            strokeOpacity: 0.4,
            icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1",
                    strokeOpacity: 1,
                    scale: 3,
                  },
                  offset: "0",
                  repeat: "12px",
                },
              ],
        },
        map: Map.map,
    });
    Map.panorama = Map.map.getStreetView();

    google.maps.event.addListener(
        Map.drawingManager,
        'polygoncomplete',
        Map.stopDrawingManager
    );

    google.maps.event.addListener(
        Map.drawingManager,
        'circlecomplete',
        Map.stopDrawingManagerCircle
    );

    google.maps.event.addListener(
        Map.drawingManager,
        'polylinecomplete',
        Map.stopDrawingManagerPolyline
    );

    if (payload?.mapOptions?.initMapCenter?.pinOnMiddle) {
        Map.markCircleOnMap({ center: mapOptions.center });
        Map.markOnMap({
            center: mapOptions.center,
            shouldBeDot: !!payload?.mapOptions?.initMapCenter?.shouldBeDot,
            action: () => { },
        });
    }
    if (payload?.inputId) {
        const input = document.getElementById(payload?.inputId) as HTMLInputElement;
        const options = {
            componentRestrictions: { country: 'br' },
            fields: ['address_components', 'geometry', 'name'],
            strictBounds: true,
        };

        const autocomplete = new google.maps.places.Autocomplete(input, options);

        newMap.addListener('bounds_changed', () => {
            setTimeout(() => {
                autocomplete.setBounds(Map.currentBounds as google.maps.LatLngBounds);
            }, 1700);
        });

        newMap.addListener('center_changed', () => {
            setTimeout(() => {
                autocomplete.setBounds(Map.currentBounds as google.maps.LatLngBounds);
            }, 1700);
        });

        autocomplete.addListener('place_changed', () => {
            const placeSelected = autocomplete.getPlace();
            if (payload.handleSelectAddress) {
                payload.handleSelectAddress(placeSelected);
            }
        });
    }
};

// loadFromJson loads and injects map options from a json file into the map cluster.
Map.loadFromJson = function (file: string) {
    Map.map.data.loadGeoJson(file);
};

// loadFromJson loads and injects map options from a json file into the map cluster.
Map.loadMultipleFromJsons = function (files: Array<string>) {
    files.forEach((file: string) => Map.map.data.loadGeoJson(file));
};

// ATTEMPT AT GENERATING POLYGONS ONE AT A TIME, WITH LISTENER AND INFOTIP
Map.generateSinglePolygonVitamin = (
    polygonData,
    color,
    onClick = (poly: any) => { }
) => {
    const coords: any[] = [];
    polygonData.coordinates[0].forEach((coordinates: any, i: number) => {
        coords[i] = new google.maps.LatLng(coordinates[1], coordinates[0]);
    });

    if (polygonData?.priority_group) {
        const group = polygonData.priority_group.split(' ')[0];
        switch (group) {
            case 'P1':
                color = '#006064';
                break;
            case 'P2':
                color = '#0288d1';
                break;
            case 'P3':
                color = '#880e4f';
                break;
            case 'P4':
                color = '#c2185b';
                break;
            case 'P5':
                color = '#ff5252';
                break;
        }
    }
    const poly = new google.maps.Polygon({
        paths: coords,
        strokeColor: '#000000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.5,
    });

    google.maps.event.addListener(poly, 'click', (_event: any): any =>
        onClick(poly)
    );
    Map.schoolPoligons.push(poly);

    poly.setMap(Map.map);
};

// generate polygons for client vitamina
Map.setVitaminPolygon = (vitaminSectors, color, action = () => { }) => {
    for (let j = 0; j < vitaminSectors.length; j++) {
        const schoolData = vitaminSectors[j];
        const coords: any[] = [];
        vitaminSectors[j].geometry.coordinates[0].forEach(
            (coordinates: any, i: number) => {
                (coords[i] = new google.maps.LatLng(coordinates[1], coordinates[0])),
                {
                    lat: coordinates[1],
                    lng: coordinates[0],
                };
            }
        );

        if (vitaminSectors[j]?.priority_group) {
            const group = vitaminSectors[j].priority_group.split(' ')[0];
            switch (group) {
                case 'P1':
                    color = '#006064';
                    break;
                case 'P2':
                    color = '#0288d1';
                    break;
                case 'P3':
                    color = '#880e4f';
                    break;
                case 'P4':
                    color = '#c2185b';
                    break;
                case 'P5':
                    color = '#ff5252';
                    break;
            }
        }

        const payload = {
            coordinates: coords,
            color,
            type: 'school',
            onClick: (poly: google.maps.Polygon) => {
                createInfoWindow(
                    Map.map,
                    Map.infoWindowSchoolsMarkers,
                    payload,
                    Map.schoolPoligons[0]
                );
                action({ schoolData: schoolData, polygon: poly });
            },
        };
        Map.drawPoligons(payload);
    }
};

Map.hideVitaminPolygons = () => {
    Map.schoolPoligons.forEach((polygon) => {
        polygon.setMap(null);
    });
};

// Create a marker with an specific action, tied to and address (or any other argument)
Map.markOnMapWithListenerAndAddress = function (payload: {
    center: TMapCoordinates;
    shouldBeDot: boolean;
    iptu: any;
    infoWindow: any;
}) {
    const infoWindow = new google.maps.InfoWindow({
        content: payload.infoWindow,
    });
    const icon = payload.shouldBeDot ? DOT_redWithBorder : PIN;
    const { map } = Map;
    const marker = new google.maps.Marker({
        position: payload.center,
        map,
        icon,
    });

    marker.addListener('click', () => {
        Map.infoWindowRealtyMarkers.forEach((item) => {
            item.close();
        });
        infoWindow.open({
            anchor: marker,
            map,
            shouldFocus: false,
        });
    });
    Map.infoWindowRealtyMarkers.push(infoWindow);
    Map.marker = marker;
};
// markOnMap marks a pin on the map.
Map.markOnMap = function (payload: {
    center: TMapCoordinates;
    shouldBeDot: boolean;
    action?: () => void;
}) {
    const icon = payload.shouldBeDot ? DOT_redWithBorder : PIN;
    const marker = new google.maps.Marker({
        position: payload.center,
        map: Map.map,
        icon,
    });
    Map.marker = marker;

    if(payload.action) {
      marker.addListener("click", payload.action)
    }
};

// markCircleOnMap marks and estabilishes a circunference on the map.
Map.markCircleOnMap = function (payload: { center: TMapCoordinates }) {
    Map.clearMap();
    Map.marker?.setMap(null);
    Map.markerCircle?.setMap(null);
    Map.map?.setCenter(
        payload.center as google.maps.LatLng | google.maps.LatLngLiteral
    );

    const markerCircle = new google.maps.Circle({
        ...defaultMapCircleStyles,

        map: Map.map,
        center: payload.center,
        radius: Map.circleRadius,
    });

    Map.markerCircle = markerCircle;
};

// setCircleRadius sets the circle radius and resizes it on the map.
Map.setCircleRadius = function (newRadius: number): void {
    Map.circleRadius = newRadius;
    Map.resizeCircleOnMap(newRadius);
};

// getCircleRadius return the circle radius.
Map.getCircleRadius = function (): number {
    return Map.circleRadius;
};

// resizeCircleOnMap resizes the circle on the map.
Map.resizeCircleOnMap = function (newRadius: number) {
    Map.markerCircle?.setRadius(newRadius);
};

// getMarkers returns the current map marker objetcs in a list.
Map.getMarkers = function (payload: IGetMarkersPayload): google.maps.Marker[] {
    const markers: google.maps.Marker[] = payload?.points?.map(
        (point: IPointWithSituation, index: number) => {
            const marker = new google.maps.Marker({
                position: point.location,
                icon: point.new ? MARKER_SPEECH_BUBBLE_BLUE : MARKER_SPEECH_BUBBLE,
                zIndex: index,
                label: {
                    text: payload.getRealtyValue(index),
                    className: point.new ? 'MARKER_SPEECH_BUBBLE_BLUE' : 'MARKER_SPEECH_BUBBLE',
                    color: '#fff',
                    fontSize: '14px',
                },
            });

            marker.addListener('click', () => payload.onMarkerClick(marker));
            return marker;
        }
    );

    return markers;
};

// markMultiplesOnMap marks multiply on the map and add an event to each marker so it can be taken by an action.
Map.markMultiplesOnMap = function (payload: IMarkMultiplesOnMapPayload) {
    const markers = Map.getMarkers({
        points: payload.points,
        onMarkerClick: payload.onMarkerClick,
        getRealtyValue: payload.getRealtyValue,
    });

    const cluster = new MarkerClusterer(Map.map, markers, markerClustercOptions);
    google.maps.event.addListener(
        cluster,
        'clusterclick',
        payload.onClusterClick
    );

    switch (payload.markerType) {
        case 0:
        default:
            Map.realtyPoints = cluster;

            setTimeout(() => {
                updateClusterValue(
                    payload.calculateClusterPriceAvrage,
                    cluster.getClusters()
                );
            }, 200);

            google.maps.event.addListener(this.map, 'idle', () => {
                setTimeout(() => {
                    updateClusterValue(
                        payload.calculateClusterPriceAvrage,
                        cluster.getClusters()
                    );
                }, 100);
            });
    }
};

// removeMarkMultiplesOnMap removes the markers from the map.
Map.removeMarkMultiplesOnMap = function (payload: { markerType: number }) {
    switch (payload.markerType) {
        case 0:
        default:
            if (Map.realtyPoints) {
                Map.realtyPoints.setMap(null);
                Map.realtyPoints.getMarkers().forEach((marker) => {
                    marker.setMap(null);
                });
            }
    }
};

// addTrafficLayer adds the traffic layer to the map.
Map.addTrafficLayer = function () {
    const trafficLayer = new google.maps.TrafficLayer();
    trafficLayer.setMap(Map.map);
    Map.trafficLayer = trafficLayer;
};

// removeTrafficLayer removes the traffic layer to the map.
Map.removeTrafficLayer = function () {
    Map.trafficLayer?.setMap(null);
};

// markMultiplesOnMapWithoutCluster builds a cluster of markers for huge chunks of data (markers).
Map.markMultiplesOnMapWithoutCluster = (
    payload: IMarkMultiplesOnMapWithoutClusterPayload
) => {
    let icon = payload.shouldBeDot ? DOT : PIN;

    const flowPoints: any = {
        gym: {
            dot: DOT_green,
            marker: MARKER_green,
        },
        university: {
            dot: DOT_lightGreen,
            marker: MARKER_lightGreen,
        },
        shoppings: {
            dot: DOT_yellowbaby,
            marker: MARKER_yellowbaby,
        },
        hospital: {
            dot: DOT_primaryred,
            marker: MARKER_primaryred,
        },
        supermarket: {
            dot: DOT_violeta,
            marker: MARKER_violeta,
        },
        subway_station: {
            dot: DOT_darkOrange,
            marker: MARKER_darkOrange,
        },
        schools: {
            dot: DOT_goldenroddark,
            marker: MARKER_goldenroddark,
        },
        banks: {
            dot: DOT_blue,
            marker: MARKER_blue,
        }
    };

    if (payload.flowPointsType) {
        icon = payload.shouldBeDot
            ? flowPoints[payload.flowPointsType].dot
            : flowPoints[payload.flowPointsType].marker;
    }

    payload.points.forEach((point) => {
        const marker = new google.maps.Marker({
            position: new google.maps.LatLng(point.point[0], point.point[1]),
            map: Map.map,
            icon,
        });

        if (point.infoWindow) {
            const infoWindow = new google.maps.InfoWindow({
                content: point.infoWindow,
            });
            Map.infoWindowFlowPoints.push(infoWindow);
            appendInfoWindowToMarker(
                Map.map,
                marker,
                infoWindow,
                Map.infoWindowFlowPoints
            );
        }

        if (point.onClick) {
            marker.addListener('click', point.onClick);
        }

        if (payload.markerType === 1) {
            Map.prospectMarkers.push(marker);
        }

        if (payload.flowPointsType) {
            Map.flowPoints = flowPointAttatcher(payload, marker, Map.flowPoints);
        }
    });
};

// removeFlowPoints remove flow points (hospital, school, pharmavy, shopping mall...) from the map.
Map.removeFlowPoints = (payload: { flowPointType: string }) => {
    Map.flowPoints.forEach((flow) => {
        if (flow.type === payload.flowPointType) {
            flow.markers.forEach((marker) => {
                marker.setMap(null);
            });
            flow.markers = [];
        }
    });
};

// setMapView sets a map view.
Map.setMapView = (payload: { view: string }) => {
    Map.map.setMapTypeId(payload.view);
};

// removeProspects removes the prospects on the map.
Map.removeProspects = () => {
    Map.prospectMarkers.forEach((marker) => {
        marker.setMap(null);
    });
};

// drawPoligons draw polygons shapes on the map.
Map.drawPoligons = function (payload: DrawPoligonsPayload) {
  const poly = new google.maps.Polygon({
      paths: payload.coordinates,
      fillColor: payload.color,
      clickable: payload.clickable,
      strokeColor: payload.strokeColor || '#000',
      strokeWeight: payload.strokeWeight || 0.5,
      strokeOpacity: payload.strokeOpacity || 0.8,
      fillOpacity: payload.fillOpacity || 0.35,
  });

    createInfoWindow(Map.map, Map.infoWindowPoligons, payload, poly);

    switch (payload.type) {
        case 'neighborhood':
            Map.neighborhoodPoligons.push(poly);
            break;
        case 'isochrone':
            Map.isochronePoligons.push(poly);
            break;
        case 'pea':
        case 'income':
            polygonAddOn(poly, payload, Map.peaPoligons, Map.incomePoligons);
            break;
        case 'school':
            Map.schoolPoligons.push(poly);
            break;
        default:
            Map.PoligonOnMap = poly;
    }

    poly.setMap(Map.map);
};

// removePoligons removes the drawns polygons.
Map.removePoligons = (type: string, id?: number) => {
    let poligonsToRemove = [] as google.maps.Polygon[];
    if (type === 'neighborhood') poligonsToRemove = Map.neighborhoodPoligons;
    if (type === 'isochrone') poligonsToRemove = Map.isochronePoligons;
    if (type === 'pea' && (id || id === 0)) {
        poligonsToRemove = Map.peaPoligons[id];
        Map.peaPoligons[id] = [];
    }
    if (type === 'income' && (id || id === 0)) {
        poligonsToRemove = Map.incomePoligons[id];
        Map.incomePoligons[id] = [];
    }

    if (type === 'school' && (id || id === 0)) {
        poligonsToRemove = Map.schoolPoligons;
    }

    poligonsToRemove?.forEach((polygon) => {
        polygon.setMap(null);
    });
};

// createStreetViewDiv creates a street view div
Map.createStreetViewDiv = (payload: {
    fenway: { lat: number; lng: number };
    div: HTMLElement;
}) => {
    const panorama = new google.maps.StreetViewPanorama(payload.div, {
        position: payload.fenway,
        pov: {
            heading: 34,
            pitch: 10,
        },
    });

    const processSVData = (
        data: google.maps.StreetViewPanoramaData | null,
        status: google.maps.StreetViewStatus
    ) => {
        if (
            status === google.maps.StreetViewStatus.OK &&
            data &&
            data.location !== undefined
        ) {
            panorama.setPano(data.location.pano);
            panorama.setPov({
                heading: 270,
                pitch: 0,
            });
            panorama.setVisible(true);
        } else {
            // eslint-disable-next-line no-console
            console.error('Street View data not found for this location.');
        }
    };
    new google.maps.StreetViewService().getPanorama(
        {
            location: payload.fenway,
            radius: 50,
            source: google.maps.StreetViewSource.OUTDOOR,
        },
        processSVData
    );
};

// stopDrawingManager if the manager which stops the drawing.\
// cleans everything that was drawn to the map.
Map.stopDrawingManager = (polygon: google.maps.Polygon) => {
    const coordinates = polygon.getPath().getArray();
    if (coordinates) {
        Map.PoligonOnMap = polygon;
    }

    if (Map.drawingManager.getDrawingMode() === null) {
        Map.PoligonOnMap.setMap(null);
    }
    Map.drawingManager.setDrawingMode(null);
};

// stopDrawingFreehand stops drawing freely.
Map.stopDrawingFreehand = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (Map.freehandListener) {
        google.maps.event.removeListener(Map.freehandListener);
        Map.freehandListener = null;
    }

    google.maps.event.clearListeners(Map.map.getDiv(), 'mousedown');
    Map.drawingManager.setDrawingMode(null);
    Map.enableMapMovement();
};

Map.drawPolyLine = (callback) => {
    if (
      Map.drawingManager.getDrawingMode() ===
      google.maps.drawing.OverlayType.POLYLINE
      
    ) {
      Map.drawingManager.setDrawingMode(null);
      Map.polylineOnMap.setMap(null);
      return;
    }
    
    google.maps.event.addListenerOnce(Map.drawingManager, 'polylinecomplete', () => {
        if (callback) callback();
    });
    Map.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYLINE);
}

Map.drawCircle = (callback) => {
    if (
      !!Map.circleOnMap?.getMap()

    ) {
      Map.drawingManager.setDrawingMode(null);
      Map.circleOnMap.setMap(null);
    }
    
    google.maps.event.addListenerOnce(Map.drawingManager, 'circlecomplete', () => {
        if (callback) callback();
    });
    Map.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
}

Map.stopDrawingManagerPolyline = (polyline: google.maps.Polyline) => {
    const coordinates = polyline.getPath().getArray();
    if (coordinates) {
        Map.polylineOnMap = polyline;
    }

    if (Map.drawingManager.getDrawingMode() === null) {
        Map.polylineOnMap.setMap(null);
    }
    Map.drawingManager.setDrawingMode(null);
};

Map.stopDrawingManagerCircle = (circle: google.maps.Circle) => {
    const center = circle.getCenter()
    const radius = circle.getRadius()
    if (center && radius) {
        Map.circleOnMap = circle;
    }

    if (Map.drawingManager.getDrawingMode() === null) {
        Map.circleOnMap.setMap(null);
    }
    Map.drawingManager.setDrawingMode(null);
};

// drawPolygon enables polygon drawing on the map.
Map.drawPolygon = (callback) => {
    Map.stopDrawingFreehand();
    if (
        Map.drawingManager.getDrawingMode() ===
        google.maps.drawing.OverlayType.POLYGON
    ) {
        Map.drawingManager.setDrawingMode(null);
        Map.PoligonOnMap.setMap(null);
        return;
    }
    google.maps.event.addListenerOnce(Map.drawingManager, 'polygoncomplete', () => {
        if (callback) callback();
    });
    Map.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
};

// stopDrawingPolygon stops polygon drawing on the map.
// calls stopDrawingManager which then cleans the whole map.
Map.stopDrawingPolygon = () => {
    Map.stopDrawingManager(Map.PoligonOnMap);
};

// return the coordinates of the drawn polygons on the map.
Map.poligonCoordinates = () => {
    const coordinates = Map.PoligonOnMap.getPath().getArray();
    if (coordinates) {
        return coordinates;
    }

    return [];
};

// disableMapMovement disables map movements.
// the idea is to "lock" the screen or make it diffucult to move unwantedly whilst drawing - mainly on mobile.
Map.disableMapMovement = () => {
    Map.map.setOptions({
        draggable: false,
        zoomControl: false,
        scrollwheel: false,
    });
};

// enableMapMovement enables map movements.
Map.enableMapMovement = () => {
    Map.map.setOptions({
        draggable: true,
        draggableCursor: 'default',
        zoomControl: true,
        scrollwheel: true,
        gestureHandling: 'cooperative',
    });
};

// drawFreeHand enables free drawing on the map.
Map.drawFreeHand = (isMobile: boolean, callback?: () => void) => {

    if (Map.freehandListener) {
        Map.stopDrawingFreehand();
        return;
    }

    const startEnvent = isMobile ? 'touchstart' : 'mousedown';

    Map.map.setOptions({ draggableCursor: 'crosshair', gestureHandling: 'none' });
    Map.freehandListener = google.maps.event.addDomListener(
        Map.map.getDiv(),
        startEnvent,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
            if (!event.ctrlKey) {
                Map.disableMapMovement();
                Map.map.setOptions({ draggableCursor: 'crosshair' });
            } else {
                Map.enableMapMovement();
            }
            const polyLine = new google.maps.Polyline({
                map: Map.map,
                ...polygonLine,
            });

            const move = google.maps.event.addListener(
                Map.map,
                'mousemove',

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (e: any) => {
                    polyLine.getPath().push(e.latLng);
                }
            );

            google.maps.event.addListenerOnce(Map.map, 'mouseup', () => {
                google.maps.event.removeListener(move);
                Map.drawingManager.setDrawingMode(null);
                callback?.();
                Map.stopDrawingFreehand();
                polyLine.setMap(null);

                const path = polyLine.getPath();

                const polygon = new google.maps.Polygon({
                    map: Map.map,
                    paths: path,
                    ...polygonFreeHandStyles,
                });

                Map.PoligonOnMap = polygon;
            });
        }
    );
};

Map.clearDrawings = ({
    cleanPea,
    cleanIncome,
} = { cleanPea: true, cleanIncome: true }) => {
    if (Map.drawingManager) {
        Map.drawingManager.setDrawingMode(null);
    }

    if (Map.schoolPoligons) {
        Map.schoolPoligons.forEach((polygon) => {
            polygon.setMap(null);
        });
    }

    if (Map.markerCircle) Map.markerCircle.setMap(null);
    if (Map.circleOnMap) Map.circleOnMap.setMap(null);
    if (Map.marker) Map.marker.setMap(null);

    Map.removePoligons('neighborhood');
    Map.removePoligons('isochrone');

    Map.infoWindowPoligons.forEach((iw) => {
        iw.close();
    });
    Map.infoWindowPoligons = [];

    if (cleanPea) {
        Map.peaPoligons.forEach((_, index) => {
            Map.removePoligons('pea', index);
        });
    }

    if (cleanIncome) {
        Map.incomePoligons.forEach((_, index) => {
            Map.removePoligons('income', index);
        });
    }

    if (Map.PoligonOnMap) Map.PoligonOnMap.setMap(null);
}

// clearMap clears everything that is on the map.
Map.clearMap = () => {
    if (Map.marker) Map.marker.setMap(null);

    Map.removeMarkMultiplesOnMap({ markerType: 0 });

    if (Map.markerCircle) Map.markerCircle.setMap(null);

    Map.flowPoints.forEach((flowPoint) => {
        flowPoint.markers.forEach((marker) => {
            marker.setMap(null);
        });
    });
    Map.flowPoints = [];

    Map.prospectMarkers.forEach((marker) => {
        marker.setMap(null);
    });
    Map.prospectMarkers = [];

    Map.removePoligons('neighborhood');
    Map.removePoligons('isochrone');

    Map.infoWindowFlowPoints.forEach((iw) => {
        iw.close();
    });
    Map.infoWindowFlowPoints = [];

    Map.infoWindowSchoolsMarkers.forEach((iw) => {
        iw.close();
    });
    Map.infoWindowSchoolsMarkers = [];

    Map.infoWindowRealtyMarkers.forEach((iw) => {
        iw.close();
    });
    Map.infoWindowRealtyMarkers = [];

    Map.infoWindowPoligons.forEach((iw) => {
        iw.close();
    });
    Map.infoWindowPoligons = [];

    Map.peaPoligons.forEach((_, index) => {
        Map.removePoligons('pea', index);
    });

    Map.incomePoligons.forEach((_, index) => {
        Map.removePoligons('income', index);
    });

    if (Map.PoligonOnMap) Map.PoligonOnMap.setMap(null);

    if (Map.freehandListener) {
        Map.stopDrawingFreehand();
    }

    if (Map.drawingManager) {
        Map.drawingManager.setDrawingMode(null);
    }

    Map.removeConcurrents('');

    if (Map.schoolsPoints) {
        Map.removeMultiplesOnMapWithInfoWindowAndAgroup();
    }
};

Map.concurrents = (
    url: string,
    coordinates: number[],
    size: number[],
    type: string
) => {
    const [width, height] = size;
    const markerSize = new google.maps.Size(width, height);

    const icon = {
        scaledSize: markerSize,
        url,
    };

    const marker = new google.maps.Marker({
        position: new google.maps.LatLng(coordinates[1], coordinates[0]),
        map: Map.map,
        icon,
        clickable: false
    });
    const hasType = Map.concurrentsMarker.find(
        (concurrent) => concurrent.type === type
    );
    if (!hasType) Map.concurrentsMarker.push({ type, markers: [] });

    Map.concurrentsMarker.forEach((concurrent) => {
        if (concurrent.type === type) concurrent.markers.push(marker);
    });
};

Map.removeConcurrents = (type: string) => {
    let markersToRemove = [] as google.maps.Marker[];
    Map.concurrentsMarker.forEach((concurrent) => {
        if (concurrent.type === type) {
            markersToRemove = concurrent.markers;
        }
        if (type === '') {
            markersToRemove = [...concurrent.markers, ...markersToRemove];
        }
    });
    markersToRemove.forEach((marker) => {
        marker.setMap(null);
    });
};

// markMultiplesOnMapWithInfoWindow builds markers for huge chunks of data (markers).
Map.markMultiplesOnMapWithInfoWindow = (
    payload: IMarkMultiplesOnMapWithInfoWindowPayload
) => {
    const icon = MARKER_darkOrange;
    payload.points.forEach((point) => {
        const marker = new google.maps.Marker({
            position: new google.maps.LatLng(
                point.coordinates[0],
                point.coordinates[1]
            ),
            map: Map.map,
            icon,
        });
        if (point.infoWindow) {
            const infowindow = new google.maps.InfoWindow({
                content: point.infoWindow,
            });
            Map.infoWindowSchoolsMarkers.push(infowindow);
            controlEventListinerOnMarker(
                Map.map,
                marker,
                infowindow,
                Map.infoWindowSchoolsMarkers
            );
        }
        if (payload.type) {
            Map.flowPoints = attatcher(payload, marker, Map.schoolsMarker);
        }
    });
};

Map.getMarkersCustom = function (payload: any): google.maps.Marker[] {
    const markers: google.maps.Marker[] = payload?.points?.map(
        (point: TMapCoordinates, index: number) => {
            const icon = MARKER_darkOrange;

            const marker = new google.maps.Marker({
                position: point,
                icon,
                // zIndex: index,
                label: {
                    text: ' ',
                    className: 'MARKER_SPEECH_BUBBLE2',
                    color: '#fff',
                    fontSize: '15px',
                },
            });

            marker.addListener('click', () => payload.onMarkerClick(marker));
            return marker;
        }
    );

    return markers;
};

Map.markMultiplesOnMapWithInfoWindowAndAgroup = function marker(
    payload: any,
    infoWindows: any
) {
    const markers = Map.getMarkersCustom({
        points: payload.points,
        onMarkerClick: payload.onMarkerClick,
    });

    const cluster = new MarkerClusterer(
        Map.map,
        markers,
        markerClustercOptionsSchools
    );
    google.maps.event.addListener(
        cluster,
        'clusterclick',
        payload.onClusterClick
    );
    cluster.setClusterClass('SchoolsCluster');

    Map.schoolsPoints = cluster;

    markers.forEach((mark, i) => {
        const infowindow = new google.maps.InfoWindow({
            content: infoWindows[i],
        });
        Map.infoWindowSchoolsMarkers.push(infowindow);
        controlEventListinerOnMarker(
            Map.map,
            mark,
            infowindow,
            Map.infoWindowSchoolsMarkers
        );
    });
};

Map.removeMultiplesOnMapWithInfoWindowAndAgroup = () => {
    Map.schoolsPoints.clearMarkers();
};

Map.plotSquareHeatMap = (drawSquareHeatMapPayload: google.maps.RectangleOptions, heatmapValue?: string) => {
    const rectangle = new google.maps.Rectangle({
        strokeWeight: 0,
        fillOpacity: 0.4,
        clickable: false,
        ...drawSquareHeatMapPayload
    });
    rectangle.setMap(Map.map);
    if (heatmapValue) {
        const infowindow = new google.maps.InfoWindow({
            content: heatmapValue,
            position: rectangle.getBounds?.()?.getCenter(),

        });
        rectangle.addListener('mouseover', () => {
          infowindow.open(Map.map);
          rectangle.setOptions({ fillOpacity: 0.8 });
        });
        rectangle.addListener('mouseout', () => {
          infowindow.close();
          rectangle.setOptions({ fillOpacity: 0.4 });
        });
    }
    Map.heatMapSquare.push(rectangle);
};

Map.removeSquareHeatMap = () => {
    Map.heatMapSquare.map((rectangle) => rectangle.setMap(null));
};

Map.plotHeatMap = (heatmapOptions?: google.maps.visualization.HeatmapLayerOptions) => {
  Map.heatMap = new google.maps.visualization.HeatmapLayer({
    ...heatmapOptions,
    map: Map.map,
  });
}

Map.removeHeatMap = () => {
  Map.heatMap?.setMap(null);
};
